import { parseCookies } from 'nookies'
import { useMemo } from 'react'
import { createClient, fetchExchange } from 'urql'

import configuration from '~/configuration'

import { AppType } from '~/core/constants/enum'
import { responseTransformer } from '~/core/utilities/responseTransformer'
import { getAuthCookiesClient, getSignedTokenClient } from '../auth/getAuthorizationCookies'
import { SESSION_COOKIE_IP } from '../constants/cookies'
import { DEFAULT_LOCALE, NODE_ENV, PUBLIC_APP_URL } from '../constants/env'

/**
 * @name useGraphQLRequest
 * @description A hook to make queries language
 */
export function useGraphQLRequest<Resp = unknown, Body = void>({
  language,
  isCareerHub,
  isTfRagGraphQL,
  isCustomReport
}: {
  language?: string
  isCareerHub?: boolean
  isTfRagGraphQL?: boolean
  isCustomReport?: boolean
}) {
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions()
  const cookies = parseCookies()

  const ip = cookies[SESSION_COOKIE_IP]
  // CHECK IN UPGRADE NEXTJS

  const isCustomDomain =
    typeof window !== 'undefined'
      ? NODE_ENV === 'development'
        ? !['localhost:3000'].includes(window.location.host as string)
        : !PUBLIC_APP_URL?.includes(window.location.host as string)
      : false

  // CHECK IN UPGRADE NEXTJS
  const isCPageUrl =
    typeof window !== 'undefined' && window.location.pathname?.toLowerCase()?.includes('/careers/')

  // Helper function to get GraphQL URL based on conditions
  const getGraphQLUrl = () => {
    // Custom domain logic

    if (isCustomDomain || isCPageUrl) {
      if (isCareerHub) {
        return configuration.api.customDomain.chubGraphQL
      }
      return configuration.api.customDomain.graphQL
    }

    // Regular domain logic
    if (isCareerHub) {
      if (isTfRagGraphQL) {
        return configuration.api.tfRagGraphQL
      }
      return configuration.api.chubGraphQL
    }

    if (isCustomReport) {
      return configuration.api.customReportGraphQL
    }

    return configuration.api.graphQL
  }

  const detectUrl = getGraphQLUrl()

  const fetchTimeout = async (input: RequestInfo | URL, init?: RequestInit | undefined) => {
    const controller = new AbortController()

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return

      if (event.data.type === 'ABORT_REQUEST') {
        controller.abort()
      }

      return
    }

    window.addEventListener('message', handleMessage)

    const response = await fetch(input, { ...init, signal: controller.signal })

    // 🚨 Enhanced error handling with app context
    if (response.status === 401) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('🚨 401 Unauthorized - Check if correct cookies are being used:', {
          isCareerHub: init?.headers && 'isCareerHub' in init.headers,
          hasAuthToken: !!(init?.headers as any)?.authorization,
          url: input
        })
      }
    }

    if (response.status === 406 && window.location.pathname !== '/406') {
      window.location.href = '/406'
    }

    return response
  }

  const client = useMemo(
    () =>
      createClient({
        url: detectUrl,
        exchanges: [responseTransformer, fetchExchange],
        fetch: fetchTimeout,
        fetchOptions: () => {
          // Read cookies fresh on each request to handle tenant switching across tabs
          const freshCookies = parseCookies()
          const appType = isCareerHub ? AppType.CHUB_APP : AppType.INTERNAL_APP
          const { sessionCookie: freshSessionCookie } = getAuthCookiesClient(freshCookies, appType)
          const freshIp = freshCookies[SESSION_COOKIE_IP]
          const signedToken = getSignedTokenClient(freshCookies, appType)

          return {
            headers: {
              ...(signedToken ? { 'X-Signed-Token': signedToken } : {}),
              authorization: `Bearer ${freshSessionCookie}`,
              language: language || DEFAULT_LOCALE,
              timezone: timeZone,
              IP: String(freshIp),
              // 🏷️ Add app context header for debugging
              'X-App-Context': isCareerHub ? 'careerhub' : 'internal',
              ...(isCustomDomain && typeof window !== 'undefined'
                ? {
                    'Access-Control-Allow-Origin': '*',
                    customDomain: `${window.location.host}/career`
                  }
                : undefined)
            }
          }
        }
      }),
    [language, ip, isCareerHub, isCustomDomain, isTfRagGraphQL, isCustomReport, timeZone]
  )

  return client
}
